import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import Input from 'components/input'
import Button from 'components/button'
import { Table, ITableData } from 'components/table'
import { Csv, Expand, Cross } from 'components/icons'
import ViewSwitch from 'views/organisation/components/viewSwitch'
import { useProject } from 'hooks/useProject'
import { bucketByDay, cumulativeSeries } from 'views/organisation/helpers/chart'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
  ChartOptions
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'chartjs-adapter-luxon'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, TimeScale, Filler)

const timeScaleOpts: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  parsing: false,
  scales: {
    // Let the time scale auto-pick its unit (day/week/month/year) from the data
    // range and cap the tick count, otherwise a multi-month/year signup range
    // crams unreadable day-level labels together.
    x: {
      type: 'time',
      bounds: 'data',
      ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 0 },
      grid: { display: false }
    },
    y: { ticks: { precision: 0 }, beginAtZero: true }
  },
  // `datalabels` is disabled explicitly: the spreadsheet chart registers
  // chartjs-plugin-datalabels *globally*, so without this every one of the
  // ~one-point-per-day series values gets stamped onto the plot, smearing the
  // chart into an unreadable mess.
  plugins: { legend: { display: false }, datalabels: { display: false } },
  elements: { point: { radius: 0, hitRadius: 6, hoverRadius: 3 } }
}

const searchInputStyle = {
  maxWidth: '200px',
  width: '200px',
  height: '40px',
  padding: '0 10px',
  boxSizing: 'border-box' as const
}

interface DataPanelProps<T> {
  title: string
  items: T[]
  getDate: (item: T) => string
  chartLabel: string
  tableData: Array<Record<string, ITableData>>
  tableColumns: { header: string; id: string }[]
  searchPlaceholder: string
  search: string
  onSearchChange: (value: string) => void
  onExport: () => void
  loading?: boolean
  fullscreen?: boolean
  extraActions?: React.ReactNode
}

function DataPanel<T>({
  title,
  items,
  getDate,
  chartLabel,
  tableData,
  tableColumns,
  searchPlaceholder,
  search,
  onSearchChange,
  onExport,
  loading = false,
  fullscreen = false,
  extraActions
}: DataPanelProps<T>) {
  const [view, setView] = useState<'chart' | 'table'>('table')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { project } = useProject()

  const daily = bucketByDay(items, getDate)
  const series = cumulativeSeries(daily)
  const chartData = {
    datasets: [
      {
        label: chartLabel,
        data: series,
        fill: true,
        borderColor: project.primaryColour,
        backgroundColor: `${project.primaryColour}1A`,
        cubicInterpolationMode: 'monotone' as const
      }
    ]
  }

  const searchInput = view === 'table' && (
    <Input
      placeholder={searchPlaceholder}
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
      style={searchInputStyle}
    />
  )

  const actions = (
    <div className="flex items-center gap-1">
      <ViewSwitch value={view} onChange={setView} />
      <Button className="h-10" onClick={onExport} internalType="outline" title="Export to CSV">
        <Csv className="mr-1" />
        Export
      </Button>
      {fullscreen &&
        (isFullscreen ? (
          <Button onClick={() => setIsFullscreen(false)} internalType="outline" title="Close" className="h-10">
            <Cross className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={() => setIsFullscreen(true)} internalType="outline" title="Fullscreen" className="h-10">
            <Expand />
          </Button>
        ))}
      {extraActions}
    </div>
  )

  const content = (
    <div className="flex-1 min-h-0 overflow-hidden">
      {view === 'chart' ? (
        <Line data={chartData} options={timeScaleOpts} />
      ) : (
        <Table data={tableData} include={tableColumns} loading={loading} defaultSort="name" defaultSortAscending />
      )}
    </div>
  )

  return (
    <>
      {isFullscreen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-surface flex flex-col z-[1]">
            <div className="flex items-center justify-between p-5 border-b border-divider-subtle">
              <h3 className="font-semibold text-body">{title}</h3>
              <div className="flex items-center gap-2">
                {searchInput}
                {actions}
              </div>
            </div>
            <div className="flex-1 p-5 overflow-auto">
              {view === 'chart' ? (
                <Line data={chartData} options={timeScaleOpts} />
              ) : (
                <Table
                  data={tableData}
                  include={tableColumns}
                  loading={loading}
                  defaultSort="name"
                  defaultSortAscending
                />
              )}
            </div>
          </div>,
          document.body
        )}
      <div className="flex flex-col min-h-[400px] xl:min-h-0 overflow-hidden">
        <h2 className="font-semibold mb-2">{title}</h2>
        <div className="flex items-center justify-between mb-3 gap-2">
          {searchInput || <div />}
          {actions}
        </div>
        {content}
      </div>
    </>
  )
}

export default DataPanel
